/**
 * The free pilot and Aithora's price, as V9 shows them (design aiBill, L26772; the Oct 28
 * "Pilot billing fix"): $99 per occupied bed per month, $0 during the free 90-day pilot, and
 * a share of added money that is off until legal review and only a Super Admin can switch.
 *
 * Where each part comes from:
 *  - The pilot: the building's setup checklist pill (GET /onboarding/facility/:id, `pilot`).
 *  - The price and occupied beds: the bill (GET /finance/billing).
 *  - What Aithora made the building during the pilot, and the share switch: no route yet
 *    (typed placeholders below; the page shows an honest "not measured yet").
 */
import { api, type PilotPill } from '@aithora/shared';
import { useFinanceApi } from './lib';

/** The published rate (STYLE.md "Numbers": $99 per bed per month, per occupied bed). */
export const RATE_PER_OCCUPIED_BED = 99;

export interface PilotState {
  /** True while the building is inside its free pilot (or signed and not live yet). */
  inPilot: boolean;
  /** Day N of the pilot, null until go-live. */
  day: number | null;
  days: number;
  code: string | null;
}

/** In the pilot while the pill is on file and the day count has not passed the pilot's length. */
export function pilotState(p: PilotPill | null | undefined): PilotState | null {
  if (!p) return null;
  const days = p.pilotDays ?? 90;
  return { inPilot: p.day === null || p.day <= days, day: p.day === null ? null : Math.min(p.day, days), days, code: p.code };
}

/** "Free pilot · day 64 of 90 · you pay $0" (design ff.payHead, L26406). */
export function pilotHead(s: PilotState): string {
  return s.day === null ? 'Free pilot · starts at go-live · you pay $0' : `Free pilot · day ${s.day} of ${s.days} · you pay $0`;
}

/** "free pilot · day 64 of 90" (design blTiles, L27300). */
export function pilotNote(s: PilotState): string {
  return s.day === null ? 'free pilot · starts at go-live' : `free pilot · day ${s.day} of ${s.days}`;
}

/** The building's pilot, from its setup checklist. Null while loading, on error, or with no pilot. */
export function usePilot(facilityId: string | null | undefined) {
  const q = useFinanceApi((c, signal) => api.facilityChecklist(c, { params: { id: facilityId! }, signal }), [facilityId], !!facilityId);
  return { pilot: pilotState(q.data?.pilot), loading: q.loading && !q.data };
}

/** One line of what Aithora added for the building (design aiBill ADD). */
export interface AddedLine {
  key: 'saved' | 'newBilling' | 'bedsFilled';
  label: string;
  note: string;
  /** Dollars, or null while nothing measures it. */
  usd: number | null;
}

/**
 * What Aithora made the building (money saved, new billing captured, beds filled). No route
 * measures these yet (PROPOSED: GET /finance/added-money?facility=&period=), so every value
 * is null and the page says "not measured yet". Never a figure until one is measured.
 */
export function addedMoney(): { measured: boolean; lines: AddedLine[] } {
  return {
    measured: false,
    lines: [
      { key: 'saved', label: 'Money saved', note: 'fewer agency shifts and overtime hours', usd: null },
      { key: 'newBilling', label: 'New billing captured', note: 'chronic care, behavioral health and advanced primary care codes billed this month', usd: null },
      { key: 'bedsFilled', label: 'Beds filled', note: 'more occupied beds than before Aithora, at the private-pay rate', usd: null },
    ],
  };
}

/**
 * The share of added money for one building (design shareCfg[facility] = { on, pct }). Off
 * by default, 0 to 50 percent, Super Admin only, and off until legal review. No route stores
 * it yet (PROPOSED: GET and PUT /finance/share?facility=), so the switch lives on this page only.
 */
export interface ShareConfig {
  on: boolean;
  pct: number;
  /** False until the back end stores the switch. */
  stored: boolean;
}
export const SHARE_DEFAULT: ShareConfig = { on: false, pct: 10, stored: false };
export const clampPct = (v: unknown) => Math.max(0, Math.min(50, Number(v) || 0));
